import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import DashboardLayout from './DashboardLayout';
import { ApiLoadState } from './ApiLoadState';
import { SearchableSelect } from './SearchableSelect';
import {
  toCreateDailySaleBody,
  useCreateDailySaleMutation,
  useGetDailySaleByIdQuery,
  useUpdateDailySaleMutation,
} from '../../redux/dailySalesApi';
import { useGetActiveLocationsQuery } from '../../redux/employeeApi';
import { useGetProductsQuery } from '../../redux/productApi';
import { useAuth } from '../../context/AuthContext';
import {
  EMPTY_DAILY_SALE,
  loadDailySales,
  saveDailySales,
} from '../../utils/billToDailySales';
import {
  dailySaleToForm,
  mapDailySaleRow,
  mergeDailySaleDetails,
  unwrapDailySale,
} from '../../utils/dailySalesList';

const LIST_PATH = '/dashboard/admin/build-to-flow/daily-sales';

const inputClasses =
  'mt-1 w-full rounded-lg border border-gray-200 bg-[#F7E5D5] px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand';

function createRowId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function upsertLocalSale(nextRow) {
  const rows = loadDailySales();
  const index = rows.findIndex((row) => String(row.id) === String(nextRow.id));
  if (index === -1) {
    saveDailySales([nextRow, ...rows]);
    return;
  }
  const next = [...rows];
  next[index] = { ...rows[index], ...nextRow };
  saveDailySales(next);
}

function AdminDailySalesAdd() {
  const { area } = useAuth();
  const isAdmin = area === 'admin';
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const previewSale = location.state?.sale;

  const [createDailySale, { isLoading: isCreating }] = useCreateDailySaleMutation();
  const [updateDailySale, { isLoading: isUpdating }] = useUpdateDailySaleMutation();
  const isSaving = isCreating || isUpdating;

  const { data, isLoading: saleLoading, error: saleError, refetch } = useGetDailySaleByIdQuery(id, {
    skip: !isEdit,
  });
  const { data: locationsRes, isLoading: locationsLoading } = useGetActiveLocationsQuery();
  const { data: productsRes, isLoading: productsLoading } = useGetProductsQuery({
    status: true,
    per_page: 500,
  });
  const [form, setForm] = useState(() =>
    previewSale ? dailySaleToForm(previewSale) : EMPTY_DAILY_SALE
  );

  const sale = useMemo(() => {
    if (!isEdit) return null;
    const unwrapped = unwrapDailySale(data);
    const fromApi = unwrapped ? mapDailySaleRow(unwrapped) : null;
    return mergeDailySaleDetails(fromApi, previewSale || null);
  }, [isEdit, data, previewSale]);

  useEffect(() => {
    if (!isEdit) return;
    const unwrapped = unwrapDailySale(data);
    const fromApi = unwrapped ? mapDailySaleRow(unwrapped) : null;
    if (!fromApi) return;
    setForm(dailySaleToForm(mergeDailySaleDetails(fromApi, previewSale || null)));
  }, [isEdit, data, previewSale]);

  const locationOptions = useMemo(() => {
    const list = locationsRes?.data || locationsRes || [];
    return (Array.isArray(list) ? list : [])
      .map((loc) => {
        const label = String(loc?.name || loc?.location_name || '').trim();
        return {
          value: label,
          label: label || String(loc?.id ?? ''),
        };
      })
      .filter((opt) => opt.value);
  }, [locationsRes]);

  const productOptions = useMemo(() => {
    const list = productsRes?.data || productsRes || [];
    if (!Array.isArray(list)) return [];
    return list
      .map((product) => {
        const itemCode = String(
          product?.item_code || product?.itemCode || product?.code || ''
        ).trim();
        if (!itemCode) return null;
        const name = String(
          product?.item_name || product?.name || product?.itemName || ''
        ).trim();
        return {
          value: itemCode,
          label: name || itemCode,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  }, [productsRes]);

  const updateForm = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!String(form.itemCode || '').trim()) {
      toast.error('Product name is required.');
      return;
    }

    const body = toCreateDailySaleBody(form);

    try {
      if (isEdit) {
        const result = await updateDailySale({ id, ...body }).unwrap();
        upsertLocalSale({
          ...EMPTY_DAILY_SALE,
          ...form,
          id,
        });
        toast.success(result?.message || 'Daily sales record updated.');
        navigate(`${LIST_PATH}/${id}`);
        return;
      }

      const result = await createDailySale(body).unwrap();
      const created =
        result?.data && typeof result.data === 'object' && !Array.isArray(result.data)
          ? result.data
          : result;
      upsertLocalSale({
        ...EMPTY_DAILY_SALE,
        ...form,
        id: created?.id || createRowId(),
      });
      toast.success(result?.message || 'Daily sales record added.');
      navigate(LIST_PATH);
    } catch (err) {
      toast.error(
        err?.data?.message || (isEdit ? 'Failed to update daily sales.' : 'Failed to add daily sales.')
      );
    }
  };

  const pageTitle = isEdit ? 'Edit Daily Sales' : 'Add Daily Sales';
  const loadFailed = Boolean(isEdit && saleError && !sale);

  return (
    <DashboardLayout title={pageTitle}>
      <div className="space-y-4 py-4">
        <div className="flex justify-end">
          <p className="text-sm text-gray-500">
            <Link
              to={isAdmin ? '/dashboard/admin' : '/dashboard/employee'}
              className="text-brand hover:underline"
            >
              Home
            </Link>
            {' / '}
            <Link to={LIST_PATH} className="text-brand hover:underline">
              Daily Sales
            </Link>
            {' / '}
            <span className="text-gray-400">{pageTitle}</span>
          </p>
        </div>

        {loadFailed ? (
          <div className="rounded-2xl bg-white p-4 shadow-sm sm:p-6">
            <ApiLoadState
              isLoading={saleLoading}
              hasData={false}
              error={saleError}
              refetch={refetch}
              message="Loading daily sale…"
              errorMessage={saleError?.data?.message || 'Unable to load daily sale details.'}
              className="py-10"
            />
          </div>
        ) : isEdit && saleLoading && !sale ? (
          <div className="rounded-2xl bg-white p-4 shadow-sm sm:p-6">
            <ApiLoadState
              isLoading
              hasData={false}
              message="Loading daily sale…"
              className="py-10"
            />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="rounded-2xl bg-white p-4 shadow-sm sm:p-6">
            <h2 className="mb-4 text-base font-semibold text-gray-900">Daily Sales Information</h2>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <label className="flex flex-col text-sm text-gray-700">
                Bill No
                <input type="text" value={form.billNo} onChange={updateForm('billNo')} className={inputClasses} />
              </label>
              <label className="flex flex-col text-sm text-gray-700">
                Bill Type
                <input
                  type="text"
                  value={form.billType}
                  onChange={updateForm('billType')}
                  placeholder="Retail"
                  className={inputClasses}
                />
              </label>
              <label className="flex flex-col text-sm text-gray-700">
                Location
                <SearchableSelect
                  name="outlet"
                  value={form.outlet}
                  onChange={updateForm('outlet')}
                  placeholder={locationsLoading ? 'Loading locations…' : 'Select location'}
                  className={inputClasses}
                  options={locationOptions}
                />
              </label>
              <label className="flex flex-col text-sm text-gray-700">
                Product Name
                <SearchableSelect
                  name="itemCode"
                  value={form.itemCode}
                  onChange={updateForm('itemCode')}
                  placeholder={productsLoading ? 'Loading products…' : 'Select product'}
                  className={inputClasses}
                  options={productOptions}
                />
              </label>
              <label className="flex flex-col text-sm text-gray-700">
                Qty Sold
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={form.quantity}
                  onChange={updateForm('quantity')}
                  className={inputClasses}
                />
              </label>
              <label className="flex flex-col text-sm text-gray-700">
                Rate
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={form.rate}
                  onChange={updateForm('rate')}
                  className={inputClasses}
                />
              </label>
              <label className="flex flex-col text-sm text-gray-700">
                Gross Amount
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={form.grossAmount}
                  onChange={updateForm('grossAmount')}
                  className={inputClasses}
                />
              </label>
              <label className="flex flex-col text-sm text-gray-700">
                Discount Amount
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={form.discountAmount}
                  onChange={updateForm('discountAmount')}
                  className={inputClasses}
                />
              </label>
              <label className="flex flex-col text-sm text-gray-700">
                Tax Rate
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={form.taxRate}
                  onChange={updateForm('taxRate')}
                  className={inputClasses}
                />
              </label>
              <label className="flex flex-col text-sm text-gray-700">
                Tax Amount
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={form.taxAmount}
                  onChange={updateForm('taxAmount')}
                  className={inputClasses}
                />
              </label>
              <label className="flex flex-col text-sm text-gray-700">
                Amount
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={form.salesAmount}
                  onChange={updateForm('salesAmount')}
                  className={inputClasses}
                />
              </label>
              <label className="flex flex-col text-sm text-gray-700">
                Unit
                <input
                  type="text"
                  value={form.unit}
                  onChange={updateForm('unit')}
                  placeholder="Kg"
                  className={inputClasses}
                />
              </label>
            </div>

            <div className="mt-6 flex justify-start gap-5">
              <button
                type="submit"
                disabled={isSaving}
                className="inline-flex min-w-[140px] items-center justify-center rounded-lg bg-brand px-8 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-red-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSaving ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                onClick={() => navigate(isEdit ? `${LIST_PATH}/${id}` : LIST_PATH)}
                className="inline-flex min-w-[140px] items-center justify-center rounded-lg border border-brand bg-white px-8 py-2.5 text-sm font-semibold text-brand hover:bg-gray-100"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </DashboardLayout>
  );
}

export default AdminDailySalesAdd;
